import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";

import {
	createBrowserRouter,
	createRoutesFromElements,
	Route,
	RouterProvider,
} from "react-router-dom";

import { store } from "./store/store.ts";
import { Provider as ReduxProvider } from "react-redux";
import { SERVER_URL } from "./config.js";

import globalAxios from "./globalAxios.js";

import { useAppSelector, useAppDispatch } from "./store/hooks.ts";
import { setLoading, setAuth } from "./store/slices/authenticateSlice.ts";
import { setUser } from "./store/slices/userSlice.ts";
import { setAnchorDate, setSessions } from "./store/slices/calendarSlice.ts";
import { setSelectedPreset, setAllFields } from "./store/slices/fieldsSlice.ts";
import { fetchPendingUploads } from "./store/slices/pendingUploadsSlice.ts";
import {
	setRegularSessions,
	setRegularSessionsLoading,
	setRegularSessionsError,
} from "./store/slices/regularSessionsSlice.ts";
import {
	setPersonals,
	setPersonalsLoading,
	setPersonalsError,
} from "./store/slices/personalsSlice.ts";

import Login from "./pages/login/Login.tsx";
import App from "./App.js";
import SearchResults from "./pages/search/SearchResults.tsx";
import SurgeonsList from "./pages/surgeon/SurgeonsList.tsx";
import SurgeonView from "./pages/surgeon/SurgeonView.tsx";
import Reports from "./pages/reports/Reports.tsx";
import Template from "./pages/template/Template.tsx";
import Calendar from "./pages/calendar/Calendar.tsx";
import PendingUploads from "./pages/pendingUploads/PendingUploads.tsx";
import Settings from "./pages/settings/Settings.tsx";
import Profile from "./pages/profile/Profile.tsx";
import Admin from "./pages/admin/Admin.tsx";

import ErrorBoundary from "./pages/fallback/ErrorBoundary.tsx";
import NotFound from "./pages/fallback/NotFound.tsx";

import ProtectedRoute, {
	AdminRoute,
} from "./components/protected/ProtectedRoute.tsx";

import { ThemeProvider } from "@mui/material";
import theme from "./themeOverride.ts";

import logo from "./SwiftMed Logo.png";

import "./index.css";

// import reportWebVitals from './reportWebVitals';

const checkPersistentSignIn = (dispatch) => {
	dispatch(setLoading(true));

	globalAxios
		.get(SERVER_URL + "/validate-session")
		.then((response) => {
			if (response.status === 200 && response.data.isValid) {
				dispatch(setAuth(true));
				dispatch(setUser(response.data.user));

				if (
					response.data.user.settings &&
					response.data.user.settings.defaultPreset
				) {
					dispatch(
						setSelectedPreset(
							response.data.user.settings.defaultPreset.name,
						),
					);
					dispatch(
						setAllFields(
							response.data.user.settings.defaultPreset.fields,
						),
					);
				} else {
					console.log("No default fields to set.");
				}

				if (
					response.data.user.settings &&
					response.data.user.settings.calendar.anchorDate
				) {
					dispatch(
						setAnchorDate(
							new Date(
								response.data.user.settings.calendar.anchorDate,
							),
						),
					);
				}

				// Fetch app data after successful auth
				fetchUserSessions(dispatch);
				fetchRegularSessions(dispatch);
				fetchPersonals(dispatch);
				dispatch(fetchPendingUploads());
			} else {
				dispatch(setAuth(false));
			}
		})
		.catch((error) => {
			console.error("Session validation failed:", error);
			dispatch(setAuth(false));
			dispatch(setLoading(false));
		})
		.finally(() => {
			dispatch(setLoading(false));
		});
};

export const fetchUserSessions = (dispatch) => {
	console.log("[index] fetchUserSessions start");

	globalAxios
		.get(SERVER_URL + "/user/sessions")
		.then((response) => {
			if (response.status === 200) {
				const sessions = response.data;
				console.log(
					"[index] fetchUserSessions success:",
					sessions?.length,
				);
				dispatch(setSessions(sessions));
			}
		})
		.catch((error) => {
			console.error("Error fetching user sessions:", error);
		});
};

export const fetchRegularSessions = (dispatch) => {
	console.log("[index] fetchRegularSessions start");

	dispatch(setRegularSessionsLoading(true));
	dispatch(setRegularSessionsError(null));

	globalAxios
		.get(SERVER_URL + "/regular-sessions")
		.then((response) => {
			if (response.status === 200) {
				const regularSessions =
					response.data?.regularSessions ??
					response.data?.regular_sessions ??
					response.data ??
					[];

				console.log(
					"[index] fetchRegularSessions success:",
					regularSessions?.length,
				);

				dispatch(setRegularSessions(regularSessions));
			} else {
				dispatch(
					setRegularSessionsError(
						"Failed to fetch regular sessions.",
					),
				);
			}
		})
		.catch((error) => {
			console.error("Error fetching regular sessions:", error);
			dispatch(setRegularSessions([]));
			dispatch(
				setRegularSessionsError("Failed to fetch regular sessions."),
			);
		})
		.finally(() => {
			dispatch(setRegularSessionsLoading(false));
		});
};

export const fetchPersonals = (dispatch) => {
	console.log("[index] fetchPersonals start");

	dispatch(setPersonalsLoading(true));
	dispatch(setPersonalsError(null));

	globalAxios
		.get(SERVER_URL + "/personals")
		.then((response) => {
			if (response.status === 200) {
				const personals =
					response.data?.personals ??
					response.data?.personal ??
					response.data ??
					[];

				dispatch(setPersonals(personals));
			} else {
				dispatch(setPersonalsError("Failed to fetch personal events."));
			}
		})
		.catch((error) => {
			console.error("Error fetching personal events:", error);
			dispatch(setPersonals([]));
			dispatch(setPersonalsError("Failed to fetch personal events."));
		})
		.finally(() => {
			dispatch(setPersonalsLoading(false));
		});
};

// Define a wrapper component for initialization
const AppInitializer = () => {
	const isLoading = useAppSelector((state) => state.authenticate.isLoading);

	const dispatch = useAppDispatch();

	useEffect(() => {
		checkPersistentSignIn(dispatch);
	}, [dispatch]);

	if (isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
				className="spinner"
			>
				<img src={logo} alt="Loading..." className="logo" />
			</div>
		);
	}

	return <RouterProvider router={router} />;
};

const router = createBrowserRouter(
	createRoutesFromElements(
		<>
			<Route path="login" element={<Login page="signin" />} />
			<Route path="signup" element={<Login page="signup" />} />
			<Route
				path="reset-password"
				element={<Login page="resetPassword" />}
			/>

			<Route
				element={<ProtectedRoute />}
				errorElement={<ErrorBoundary />}
			>
				<Route path="/" element={<App />}>
					<Route index element={<Calendar />} />
					<Route path="search" element={<SearchResults />} />
					<Route path="surgeons" element={<SurgeonsList />} />
					<Route path="surgeons/:id" element={<SurgeonView />} />
					<Route path="template" element={<Template />} />
					<Route path="reports" element={<Reports />} />
					<Route path="calendar" element={<Calendar />} />
					<Route path="pending" element={<PendingUploads />} />
					<Route path="settings" element={<Settings />} />
					<Route path="profile" element={<Profile />} />
					<Route element={<AdminRoute />}>
						<Route path="admin" element={<Admin />} />
					</Route>
				</Route>
			</Route>

			<Route path="*" element={<NotFound />} />
		</>,
	),
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
	<React.StrictMode>
		<ReduxProvider store={store}>
			<ThemeProvider theme={theme}>
				<AppInitializer />
			</ThemeProvider>
		</ReduxProvider>
	</React.StrictMode>,
);

// reportWebVitals();
